"use client";

import {
  AVATAR_GENERICO,
  DEFAULT_BADGE,
  LOGO_DARK,
  WIDTH_MD,
} from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";

export interface PreviewCertificateProps {
  name: string;
  duration: string | undefined;
  subtitle: string | undefined;
  badge: string | undefined;
  certificateLabel: string | undefined;
  vendorImage?: string;
  userName?: string;
  userLastName?: string;
  preview: boolean;
  issueDate?: Date;
  certificateTopLabel?: string;
  userProfileImage?: string;
  userRole?: string;
}

function formatISODate(isoDate: string, isMd = false): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: isMd ? "long" : "short",
    year: "numeric",
  });
}

/**
 * Lienzo visual del certificado (contenido sólido de producto).
 * Sin min-h-screen, sin cards shadow-xl, sin headers gradient/backdrop-blur.
 */
export function PreviewCertificate({
  name,
  duration,
  subtitle,
  badge,
  certificateLabel,
  vendorImage,
  userName,
  userLastName,
  preview,
  issueDate,
  certificateTopLabel,
  userProfileImage,
  userRole,
}: PreviewCertificateProps) {
  const wWidth = useWindowWidth();
  const isMd = wWidth >= WIDTH_MD;
  const { t } = useTranslation("common");

  const certificateDate = issueDate
    ? formatISODate(issueDate.toString(), isMd)
    : null;
  const currentDate = formatISODate(new Date().toISOString(), isMd);
  const fullName = `${userName || ""} ${userLastName || ""}`.trim();
  const avatarSrc =
    userProfileImage === "some-default-url-avatar-for-users"
      ? AVATAR_GENERICO
      : userProfileImage || AVATAR_GENERICO;

  const resolvedTopLabel =
    certificateTopLabel || t("Titulo superior del certificado");
  const resolvedName = name || t("Nombre del certificado");
  const resolvedSubtitle =
    subtitle || (!preview ? "" : t("Subtitulo del certificado"));
  const resolvedLabel =
    certificateLabel ||
    (!preview ? "" : t("Etiqueta del certificado"));
  const resolvedDuration =
    duration || (!preview ? "" : t("Duración"));
  const displayName =
    fullName.length > 20
      ? `${userName?.split(" ")[0] || ""} ${userLastName || ""}`.trim()
      : fullName;

  return (
    <div className="flex w-full items-center justify-center overflow-hidden rounded-2xl bg-[url('/images/png/background-certificate.png')] bg-cover bg-center p-4 sm:p-5">
      <div className="flex w-full flex-col gap-4 px-3 sm:px-6 md:px-10">
        <div className="space-y-1 text-left text-white">
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-white/90">
            {resolvedTopLabel}
          </p>
          <h3 className="text-[13px] font-semibold leading-snug sm:text-[15px]">
            {resolvedName}
          </h3>
          {resolvedSubtitle ? (
            <p className="text-[9px] leading-snug text-white/70">
              {resolvedSubtitle}
            </p>
          ) : null}
        </div>

        <div className="relative rounded-xl border border-cyan-500/80 p-3 sm:p-4">
          {resolvedLabel ? (
            <div className="absolute -top-2.5 left-3 rounded-full bg-cyan-500 px-2 py-0.5 text-[10px] font-semibold text-white sm:px-3">
              {resolvedLabel}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full sm:h-20 sm:w-20">
              <Image
                src={avatarSrc}
                alt=""
                className="object-cover"
                fill
                sizes="80px"
              />
            </div>

            <div className="min-w-0 flex-1 leading-tight text-white">
              <p className="truncate text-[13px] font-medium capitalize leading-snug">
                {displayName || t("Usuario")}
              </p>
              <p className="truncate text-[9px] leading-snug text-white/70">
                {userRole || "Ciudadano"}
              </p>
              {resolvedDuration ? (
                <p className="truncate text-[9px] leading-snug text-white/70">
                  {resolvedDuration}
                </p>
              ) : null}
              <p className="truncate text-[9px] leading-snug text-white/70">
                {t("Finalizado el", {
                  certificateDate: certificateDate || currentDate,
                })}
              </p>
            </div>

            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full sm:h-16 sm:w-16 md:h-20 md:w-20">
              {badge || !preview ? (
                <Image
                  src={badge || DEFAULT_BADGE}
                  alt=""
                  width={80}
                  height={80}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center rounded-full bg-slate-600 text-center text-[9px] leading-snug text-white">
                  {t("insignia")}
                  <br />
                  110x110 px
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 items-center gap-3">
          <div className="relative flex h-10 items-center justify-start">
            {vendorImage ? (
              <Image
                src={vendorImage}
                alt=""
                className="max-h-full max-w-full object-contain object-left"
                fill
                sizes="160px"
              />
            ) : preview ? (
              <p className="text-[9px] leading-snug text-white/70">
                {t("insignia")}
                <br />
                160x40 px
              </p>
            ) : null}
          </div>
          <div className="relative h-6 w-full">
            <Image
              src={LOGO_DARK || "/fallback-logo.png"}
              alt=""
              className="object-contain object-right"
              fill
              sizes="120px"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
